import React, { useEffect, useState, useRef, useContext } from "react";
import { format, addDays, startOfDay } from 'date-fns'
import { useQuery, useQueryClient } from "react-query";
import { clearCookies, insightsApi } from "../../services/api";
import { useInsightsData } from '../../contexts/insightsData';
import Calendar, { ICalendarPicker } from "./components/Calendar/Calendar";
import Error from "./components/Error";
import AppEnabled from "../appEnabled";
import AppDisabled from "../appDisabled";
import SkeletonAppDisabled from "../appDisabled/Skeleton";
import SkeletonAppEnabled from "../appEnabled/Skeleton";
import * as Vlab from 'vlab-frontend-components'
import { IAM_URL } from "../../utils/constants";
import { readOrgCookie, writeOrgCookie } from "../../utils/org-cookie";
import { useTranslation } from "react-i18next";
import { FnsDateStateContext } from "../../contexts/fnsDate";
import type { InsightsData } from '../../contexts/insightsData';
import styled from 'styled-components';
import mixpanel from "mixpanel-browser";

const StyledButtonIconRefresh = styled(Vlab.ButtonIconRefresh)`
  &:hover {
    background-color: #ecf2f8;
  }
`;

type SelectProps = {
    label: any,
    value: string
}

const initialPaginationValues = {
    currentPage: 0,
    pageSize: 4,
    total: 0,
    totalPages: 0,
    detailsLevel: ''
};

export function HomePage() {
    const { insightsData, setInsightsData } = useInsightsData();
    const { t } = useTranslation();
    const fnsDate = useContext(FnsDateStateContext)

    const calendarRef = useRef<HTMLDivElement>(null);
    const filterButtonRef = useRef<HTMLButtonElement>(null);

    const [selectedOrganization, setSelectedOrganization] = useState<string>("");
    const [organizationsOptions, setOrganizationsOptions] = useState<SelectProps[]>([]);
    const [formattedDateRange, setFormattedDateRange] = useState<string>(t('home.noPeriodSelected'));
    const [isFilter, setIsFilter] = useState(false);
    const [lowfetching, setLowfetching] = useState(false);
    const [isAppEnabled, setIsAppEnabled] = useState(false);
    const [pagination, setPagination ] = useState(initialPaginationValues)
    const queryClient = useQueryClient();

    const [enableRequest, setEnableRequest] = useState({
        selectedDate: true,
        onlyOrg: true,
    });

    const now = Date.now();
    const startOfToday = startOfDay(addDays(now, -1));

    const [rangeDate, setRangeDate] = useState<ICalendarPicker>({
        startDate: addDays(startOfToday, -29),
        endDate: startOfToday,
        key: 'selection',
    });

    const getProfileData = async () => {
            const resultProfile = await insightsApi.getProfile()
            return resultProfile
        }
    
    const profileDataQuery = useQuery('profileData', getProfileData, {
        onError: (error: any) => {
        }
    })

    const { data: profileData } = profileDataQuery

    mixpanel.init("c56e9d183baa82de0879a7d35b32a033", {
        persistence: "localStorage",
    });

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const targetElement = event.target as HTMLElement;

            if (targetElement.closest && targetElement.closest('.ignore-click-outside')) {
                return;
            }

            if (
                (!filterButtonRef.current || !filterButtonRef.current.contains(targetElement)) &&
                (!calendarRef.current || !calendarRef.current.contains(targetElement))
            ) {
                setIsFilter(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isFilter]);

    useEffect(() => {
        if (selectedOrganization)
            setEnableRequest(prevActivate => ({ ...prevActivate, onlyOrg: true }));
    }, [pagination.currentPage, pagination.detailsLevel]);

    useEffect(() => {
        const formattedStartDate = fnsDate?.formatDateToString(rangeDate.startDate);
        const formattedEndDate = format(rangeDate.endDate, 'dd/MM/yy');
        setFormattedDateRange(`${formattedStartDate} até ${formattedEndDate}`);
    }, [rangeDate, fnsDate?.fnsDate]);

    const { data: organizations, isLoading: organizationsLoading, error: organizationsError } = useQuery(
        "organizations",
        insightsApi.getOrganizationsUser,
        {
            onSuccess: (data) => {
                if (data.organizations && data.organizations.length > 0) {
                    const savedOrg = readOrgCookie();
                    const initialOrg =
                        data.organizations.find((org: any) => org.code === savedOrg) ?? data.organizations[0];
                    setSelectedOrganization(initialOrg.code);
                    writeOrgCookie(initialOrg.code);
                    setOrganizationsOptions(data.organizations.map((org: any) => ({ value: org.code, label: org.description })))
                    setIsAppEnabled(initialOrg.app_enabled);
                    setPagination({...pagination, detailsLevel: "" });
                    setEnableRequest({ selectedDate: false, onlyOrg: true });
                }
            },
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchInterval: false,
            onError: (error: any) => {
            },
        }
    );

    const getInsightsData = async (
        startDate: Date,
        endDate: Date, 
        organization: string,
        detailsLevel: string = ''
    ) => {
        try {
            const formattedStartDate = format(startDate, 'yyyy-MM-dd');
            const formattedEndDate = format(endDate, 'yyyy-MM-dd');

            const response = await insightsApi.getData(
                organization, 
                formattedStartDate, 
                formattedEndDate, 
                pagination.currentPage, 
                pagination.pageSize,
                detailsLevel
            );

            let result;        
            if (!detailsLevel || detailsLevel === '') {
                result = {
                    ...response,
                    feedbacks: response.feedbacks.feedback,
                    ratings: response.ratings.ratings,
                    ratingsAverage: response.ratings.average
                };
                
                setPagination({
                    ...pagination,
                    total: response.ratings.total,
                    totalPages: Math.ceil(response.ratings.total / response.ratings.page_size)
                });
            } 
            else if (detailsLevel === 'feedbacks') {
                result = {
                    ...insightsData,
                    feedbacks: response.feedbacks.feedback
                };
                
                setPagination({
                    ...pagination,
                    total: response.feedbacks.total,
                    totalPages: Math.ceil(response.feedbacks.total / response.feedbacks.page_size)
                });
            } 
            else if (detailsLevel === 'ratings') {
                result = {
                    ...insightsData,
                    ratings: response.ratings.ratings,
                    ratingsAverage: response.ratings.average
                };
                
                setPagination({
                    ...pagination,
                    total: response.ratings.total,
                    totalPages: Math.ceil(response.ratings.total / response.ratings.page_size)
                });
            }

            return result;
        } catch (err) {
            console.error('Erro em getInsightsData:', err);
            throw err;
        }
    };

    const { isFetching, isLoading, error, refetch } = useQuery(
        [
            'insightsData',
            selectedOrganization,
            rangeDate.startDate,
            rangeDate.endDate, 
            pagination.currentPage,
            pagination.detailsLevel, 
        ],
        () => getInsightsData(
            rangeDate.startDate, 
            rangeDate.endDate, 
            selectedOrganization,
            pagination.detailsLevel
        ),
        {
            enabled: !!selectedOrganization && !!rangeDate.startDate && !!rangeDate.endDate && (enableRequest.selectedDate || enableRequest.onlyOrg),
            onSuccess: (data) => {
                setInsightsData(data);
                setEnableRequest({ selectedDate: false, onlyOrg: false });
                setIsFilter(false);

                const formattedStartDate = format(rangeDate.startDate, 'yyyy-MM-dd');
                const formattedEndDate = format(rangeDate.endDate, 'yyyy-MM-dd');
                const trackingData = {
                    distinct_id: selectedOrganization,
                    USER: profileData?.profileInfo?.user?.identity,
                    initial_date: formattedStartDate,
                    final_date: formattedEndDate,
                    IS_VLAB: profileData?.profileInfo?.user?.is_vlab,
                };
                mixpanel.track("WEB_APP_INSIGHTS", trackingData);
            },
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchInterval: false,
            cacheTime: 0,
            onError: (error: any) => {
            },
        },
    );

    const handleRefresh = () => {
        const cacheData = queryClient.getQueryData([
            'insightsData',
            selectedOrganization,
            rangeDate.startDate,
            rangeDate.endDate, 
            pagination.currentPage,
            pagination.detailsLevel, 
        ]);

        if (
            cacheData &&
            typeof cacheData === 'object' &&
            'logo_url' in cacheData &&
            'organization' in cacheData &&
            'insight' in cacheData
        ) {
            setInsightsData(cacheData as InsightsData);
            setLowfetching(false);
        } else {
            refetch();
            setLowfetching(false);
        }
    };

    useEffect(() => {
        if (isFetching) {
            setLowfetching(false);
        }

        const timeoutId = setTimeout(() => {
            if (isFetching) {
                setLowfetching(true);
            }
        }, 15000);

        return () => clearTimeout(timeoutId);
    }, [isFetching]);

    const renderContent = () => {
        if ((isLoading || organizationsLoading || isFetching) && pagination.detailsLevel === "") {
            return isAppEnabled ? <SkeletonAppEnabled /> : <SkeletonAppDisabled />;
        }

        if (!insightsData || !organizations) {
            return <Error label={t('home.notFoundData')} />;
        }

        if (error || organizationsError) {
            return <Error label={t('home.errorLoadingData')} />;
        }

        if (lowfetching) {
            return <Error label={t('home.timeoutError')} />;
        }

        return isAppEnabled ? (
            <AppEnabled
                insightsData={insightsData}
                selectedOrganization={selectedOrganization}
                dateRange={rangeDate}
                pagination={pagination}
                setPagination={setPagination}
                setEnableRequest={setEnableRequest}
                isLoading={isFetching}
            />
        ) : (
            <AppDisabled
                insightsData={insightsData}
                dateRange={rangeDate}
                pagination={pagination}
                setPagination={setPagination}
            />
        );
    };
    

    const handleCalendarDateChange = (newRangeDate: ICalendarPicker) => {
        if (newRangeDate) {
            setRangeDate(newRangeDate);
        }
    };

    const buttonActivate = () => {
        setEnableRequest(prevActivate => ({ ...prevActivate, selectedDate: true }));
    };

    const handleOrganizationChange = (value: any) => {
        if (isFetching) {
            console.warn('Troca de organização bloqueada durante carregamento!');
            return;
        }
        const selectedOrg = value;
        setSelectedOrganization(selectedOrg);
        writeOrgCookie(selectedOrg);
        verifyIsAppEnable(selectedOrg);
        setEnableRequest(prevActivate => ({ ...prevActivate, onlyOrg: true }));
        setPagination({ ...pagination, currentPage: 0, detailsLevel: "" });
        setInsightsData({
            logo_url: '',
            organization: '',
            frame_url: null,
            insight: {
                baby_users_quantity: 0,
                booking_clicks: 0,
                content_article_read: 0,
                content_video_play: 0,
                content_view_quantity: 0,
                curiosity_share: 0,
                exam_view_quantity: 0,
                frame_share_quantity: 0,
                insight_date: '',
                live_view_quantity: 0,
                logo_share: 0,
                most_popular_baby_name: null,
                most_shared_frame: null,
                most_viewed_content_type: null,
                organization_code: '',
                top_viewed_content: null,
                total_exams: 0,
            },
            insight_list: [],
            feedbacks: [],
        });
    }

    const verifyIsAppEnable = (selectedOrg: string) => {
        const selectedOrgData = organizations.organizations.find(
            (org: { code: string }) => org.code === selectedOrg
        );

        const orgEnabled = selectedOrgData ? selectedOrgData.app_enabled : false;
        setIsAppEnabled(orgEnabled);
    }

    const handleFilterButtonClick = () => {
        setIsFilter((prevIsFilter) => !prevIsFilter);
    };

    return (
        <main className="w-[100%] pt-[86px]">
            <header className="flex mobile:flex-col mobile:space-y-3 justify-between desktop:flex-row laptop:flex-row tablet:flex-row ">

                <div className="items-center">
                    <Vlab.TitleAutoComplete
                        onChange={(value) => handleOrganizationChange(value)}
                        placeholder=''
                        title={t('components.selectOrganization')}
                        options={organizationsOptions}
                        value={selectedOrganization}
                        innerStyles={{
                            autoCompleteWrapper: { height: '33px', width: '277px' },
                            dropdownContent: { width: '277px' },
                            input: { fontSize: '12px' }
                        }}
                    />
                </div>
                <div className="flex desktop:ml-6 tablet:ml-6 laptop:ml-6 flex-wrap-nowrap items-center desktop:space-x-4 tablet:space-x-4 laptop:space-x-4 mobile:space-x-3 ignore-click-outside">
                    <Vlab.Typography
                        type="poppinsBodyM"
                        color={formattedDateRange === t('home.noPeriodSelected') ? "greyMedium" : "blackLight"}
                        className="flex-wrap-nowrap whitespace-nowrap"
                    >
                        {fnsDate?.fnsDate.formatDateToString(rangeDate.startDate)} {t('components.calendar.to')} {fnsDate?.fnsDate.formatDateToString(rangeDate.endDate)}
                    </Vlab.Typography>
                    <Vlab.FilterButton 
                        name={t('components.filter')} 
                        variant="primary" 
                        open={isFilter} 
                        onClick={handleFilterButtonClick}
                    />
                
                    <StyledButtonIconRefresh
                        onClick={handleRefresh}
                        variant="primary"
                        iconColor="#34353D"
                        title={t('components.refreshButton')}
                        disabled={isFetching}
                        style={{
                            height: '32px',
                            padding: '8px',
                            border: 'none'
                        }}
                    >
                    </StyledButtonIconRefresh>
                </div>

                {isFilter && (
                    <div
                        className="border-none rounded-lg absolute top-32 mt-2 right-8 "
                        ref={calendarRef}
                    >
                        <Calendar getDate={handleCalendarDateChange} range={[rangeDate]} onApplyButtonClick={buttonActivate} />
                    </div>
                )}
            </header>

            {renderContent()}
        </main>
    )
}